import { dealsApi } from "@/api/deals-api";
import { getStageText } from "@/lib/utils";
import { useUser } from "@clerk/clerk-react";
import { useDealsListStore } from "@/store/deals-list-store";
import { TOUR_TYPES, useTourStore } from "@/store/tour-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useDealsViewStore } from "@/store/deals-view-store";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";
import { Skeleton } from "../ui/skeleton";
import { DealsEmptyOnboarding } from "./empty-onboarding";
import { FilterValues, SOURCE_NONE } from "./filter-sheet";
import { MobileView } from "./mobile-view";
import { TableView } from "./table-view";
import { KanbanView } from "./kanban-view";
import { Toolbar } from "./toolbar";
import { DealStatus } from "./types";
import { StageFunnelStrip } from "./stage-funnel-strip";
import { hasFitScore, getFitScore, getFitScoreBucket } from "./utils";
import { getLegacyPendingIndustryEntries } from "@/lib/deal-field-state";
import { useDealsListReportStatusSocket } from "@/hooks/use-deals-list-report-status-socket";

// useMediaQuery — listen for viewport changes (replaces the buggy
// `window.innerWidth` snapshot at render that didn't update on resize).
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

export const DealsComponent = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dealStatuses, setDealStatuses] = useState<Record<string, string>>({});
  const { deals, isLoading, fetchDeals, updateDeal } = useDealsListStore();
  const { activeWorkspaceId, activeWorkspace } = useWorkspaceStore();
  const { view } = useDealsViewStore();
  const { user } = useUser();
  const [activeFilters, setActiveFilters] = useState<FilterValues>({
    industries: [],
    statuses: [],
    stages: [],
    hasPitchDeck: false,
    fitScoreBuckets: [],
    sources: [],
    dateRange: { from: undefined, to: undefined },
  });
  const { startTour } = useTourStore();
  const isMobileView = useMediaQuery("(max-width: 767px)");
  const [searchParams, setSearchParams] = useSearchParams();

  // Consume `?status=<stage>` whenever present and the workspace is loaded.
  // Validates against the workspace's own statuses so a stale or cross-workspace
  // link surfaces a toast instead of silently filtering to an empty board.
  useEffect(() => {
    if (!activeWorkspace) return;
    const statusParam = searchParams.get("status");
    if (!statusParam) return;

    const validTitles = new Set(
      activeWorkspace.statuses?.map((s) => s.title) ?? []
    );
    if (validTitles.has(statusParam)) {
      setActiveFilters((prev) => ({
        ...prev,
        statuses: [statusParam as DealStatus],
      }));
    } else {
      toast.error(
        `Stage "${statusParam}" doesn't exist in this workspace.`
      );
    }

    const next = new URLSearchParams(searchParams);
    next.delete("status");
    setSearchParams(next, { replace: true });
  }, [activeWorkspace, searchParams, setSearchParams]);

  // Fetch deals when workspace changes
  useEffect(() => {
    if (activeWorkspaceId) {
      fetchDeals(activeWorkspaceId);
    }
  }, [activeWorkspaceId, fetchDeals]);

  // Keep every row's report-status indicator live (not just the open deal).
  useDealsListReportStatusSocket(activeWorkspaceId);

  // Start tour when deals are loaded
  useEffect(() => {
    if (Object.keys(deals).length > 0) {
      if (isMobileView) {
        startTour(TOUR_TYPES.MOBILE_DEALS_TABLE);
      } else {
        startTour(TOUR_TYPES.DEALS_TABLE);
      }
    }
  }, [deals, isMobileView, startTour]);

  // Initialize dealStatuses from deals when they load
  useEffect(() => {
    const initialStatuses = Object.entries(deals).reduce(
      (acc, [id, deal]) => ({
        ...acc,
        [id]: deal.overview.status,
      }),
      {}
    );
    setDealStatuses(initialStatuses);
  }, [deals]);

  // Diagnostic: surface deals carrying legacy `industries` / `industry`
  // entries that are pending in BE but unreachable from the FE (the
  // Profile tab only renders `sub_industries`). Logged so we can spot
  // deals that need cleanup upstream — the pipeline badge intentionally
  // ignores these to avoid dead-end "Review industries" clicks.
  useEffect(() => {
    if (Object.keys(deals).length === 0) return;
    const offenders = Object.values(deals)
      .map((deal) => ({
        id: deal.id,
        name: deal.overview.name,
        legacy: getLegacyPendingIndustryEntries(deal),
      }))
      .filter((row) => row.legacy.length > 0);
    if (offenders.length > 0) {
      console.warn(
        `[deals] ${offenders.length} deals have legacy pending industry entries (industries/industry field, unreachable from Profile tab):`,
        offenders,
      );
    }
  }, [deals]);

  const editDealStatusMutation = useMutation<
    unknown,
    Error,
    { dealId: string; status: string },
    { previousDeal: (typeof deals)[string]; previousStatus: string }
  >({
    mutationFn: async ({ dealId, status }) => {
      return dealsApi.changeStatus(
        dealId,
        status,
        user?.id ?? "",
        activeWorkspaceId ?? ""
      );
    },
    onMutate: async ({ dealId, status }) => {
      const existingDeal = deals[dealId];
      if (!existingDeal) throw new Error("Deal not found");
      // Fall back to the deal's own status when dealStatuses hasn't tracked
      // this deal yet (first mutation in session). Otherwise Undo would call
      // mutate with status=undefined and corrupt the server-side value.
      const previousStatus =
        dealStatuses[dealId] ?? existingDeal.overview.status;
      const updatedDeal = {
        ...existingDeal,
        overview: {
          ...existingDeal.overview,
          status: status,
        },
      };
      updateDeal(dealId, updatedDeal);
      setDealStatuses((prev) => ({
        ...prev,
        [dealId]: status,
      }));
      return { previousDeal: existingDeal, previousStatus };
    },
    onError: (_err, { dealId }, context) => {
      if (context) {
        updateDeal(dealId, context.previousDeal);
        setDealStatuses((prev) => ({
          ...prev,
          [dealId]: context.previousStatus,
        }));
      }
      toast.error(getErrorMessage(_err, "Failed to update deal status"));
    },
    onSuccess: (_data, { dealId, status: newStatus }, context) => {
      // If the moved deal would no longer match the active stage filter,
      // auto-clear the filter and surface a toast with Undo so the user
      // doesn't think the deal vanished.
      const wouldFallOutOfView =
        activeFilters.statuses.length > 0 &&
        !activeFilters.statuses.includes(newStatus);

      if (wouldFallOutOfView) {
        setActiveFilters((prev) => ({ ...prev, statuses: [] }));
        toast.success(`Moved to ${newStatus}`, {
          action: {
            label: "Undo",
            onClick: () => {
              if (context) {
                editDealStatusMutation.mutate({
                  dealId,
                  status: context.previousStatus,
                });
              }
            },
          },
        });
      } else {
        toast.success("Deal status updated successfully");
      }
    },
  });

  // Compute deal counts per status (from ALL deals, before filtering)
  const dealCountByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.values(deals).forEach((deal) => {
      const status = dealStatuses[deal.id] || deal.overview.status;
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [deals, dealStatuses]);

  // Multi-toggle: clicking a strip pill toggles that status in/out of the
  // statuses filter. Clicking "All" (null) clears the filter entirely.
  const handleStageFilterClick = (status: string | null) => {
    setActiveFilters((prev) => {
      if (status === null) {
        return { ...prev, statuses: [] };
      }
      const exists = prev.statuses.includes(status);
      const next = exists
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status];
      return { ...prev, statuses: next as DealStatus[] };
    });
  };

  // Filter deals based on search and filters
  const filteredDeals = useMemo(
    () =>
      Object.values(deals)
        .filter((deal) => {
          // Search filter
          if (
            !deal.overview.name
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          ) {
            return false;
          }

          // Industry filter
          if (
            activeFilters.industries.length > 0 &&
            !deal.overview.industry?.some((i: string) =>
              activeFilters.industries.includes(i)
            )
          ) {
            return false;
          }

          // Status filter
          if (activeFilters.statuses.length > 0) {
            const effectiveStatus =
              dealStatuses[deal.id] || deal.overview.status;
            if (
              !activeFilters.statuses.includes(effectiveStatus as DealStatus)
            ) {
              return false;
            }
          }

          // Stage filter
          if (activeFilters.stages.length > 0) {
            const dealStageText = getStageText(deal.overview.companyStage);
            const filterStageTexts = activeFilters.stages.map((s) =>
              getStageText(s)
            );
            if (!filterStageTexts.includes(dealStageText)) {
              return false;
            }
          }

          // Pitch deck filter
          if (
            activeFilters.hasPitchDeck &&
            !deal.company_files?.some(
              (file) => file.file_type === "company pitchdeck"
            )
          ) {
            return false;
          }

          // Fit Score bucket filter
          if (activeFilters.fitScoreBuckets.length > 0) {
            const bucket = hasFitScore(deal)
              ? getFitScoreBucket(getFitScore(deal))
              : "unanalyzed";
            if (!activeFilters.fitScoreBuckets.includes(bucket)) {
              return false;
            }
          }

          // Source filter
          if (activeFilters.sources.length > 0) {
            const src = deal.overview.source;
            const matches = src
              ? activeFilters.sources.includes(src)
              : activeFilters.sources.includes(SOURCE_NONE);
            if (!matches) return false;
          }

          // Date range filter — clone the `to` date before setHours so we
          // don't mutate the Date stored in activeFilters state on every memo
          // recomputation (date pickers read the original).
          if (activeFilters.dateRange?.from || activeFilters.dateRange?.to) {
            const dealDate = new Date(deal.overview.addedOn);
            if (
              activeFilters.dateRange.from &&
              dealDate < activeFilters.dateRange.from
            ) {
              return false;
            }
            if (activeFilters.dateRange.to) {
              const endOfDay = new Date(activeFilters.dateRange.to);
              endOfDay.setHours(23, 59, 59, 999);
              if (dealDate > endOfDay) {
                return false;
              }
            }
          }

          return true;
        })
        .map((deal) => ({
          ...deal,
          overview: {
            ...deal.overview,
            status: dealStatuses[deal.id] || deal.overview.status,
          },
        })),
    [deals, dealStatuses, searchQuery, activeFilters]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Toolbar skeleton */}
        <div className="flex flex-col gap-2 px-4 py-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 flex-1 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
            <Skeleton className="h-8 w-24 rounded-md" />
          </div>
        </div>

        {/* Stage funnel strip skeleton */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5 px-3">
              <Skeleton className="h-6 w-8 rounded" />
              <Skeleton className="h-3 w-14 rounded" />
            </div>
          ))}
        </div>

        {/* Table skeleton */}
        <div className="flex-1 overflow-hidden px-2">
          {/* Header */}
          <div className="flex items-center gap-4 border-b border-border py-3 px-4">
            <Skeleton className="h-3 w-4 rounded" />
            <Skeleton className="h-3 w-24 rounded" />
            <Skeleton className="h-3 w-16 rounded" />
            <Skeleton className="h-3 w-16 rounded" />
            <Skeleton className="h-3 w-12 rounded" />
            <Skeleton className="h-3 w-20 rounded" />
            <Skeleton className="h-3 w-16 rounded" />
            <Skeleton className="h-3 w-20 rounded" />
          </div>
          {/* Rows */}
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-4 border-b border-border/40 py-3.5 px-4"
            >
              <Skeleton className="h-4 w-4 rounded" />
              <div className="flex items-center gap-2 w-[30%]">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-1.5 flex-1">
                  <Skeleton
                    className="h-3.5 rounded"
                    style={{ width: `${50 + Math.random() * 30}%` }}
                  />
                  <Skeleton className="h-2.5 w-3/4 rounded" />
                </div>
              </div>
              <Skeleton className="h-5 w-14 rounded" />
              <Skeleton className="h-5 w-14 rounded" />
              <Skeleton className="h-4 w-10 rounded" />
              <Skeleton className="h-4 w-20 rounded" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-16 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasNoDeals = Object.keys(deals).length === 0;

  if (hasNoDeals) {
    return <DealsEmptyOnboarding />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Toolbar
        onSearch={setSearchQuery}
        onFiltersChange={setActiveFilters}
        deals={deals}
        dealCountByStatus={dealCountByStatus}
      />

      {/* Stage Funnel Strip — hidden in Kanban view because the columns
          ARE the stage filter; showing both is redundant. */}
      {view !== "kanban" &&
        activeWorkspace?.statuses &&
        activeWorkspace.statuses.length > 0 && (
          <StageFunnelStrip
            statuses={activeWorkspace.statuses}
            dealCountByStatus={dealCountByStatus}
            activeStatuses={activeFilters.statuses}
            onStatusClick={handleStageFilterClick}
          />
        )}

      <div className="flex-1 overflow-hidden">
        {/* Mobile view for small screens */}
        <div
          className="block md:hidden h-full overflow-y-auto"
          data-tour="mobile-container"
        >
          <MobileView
            deals={filteredDeals}
            onStatusChange={editDealStatusMutation.mutate}
            workspaceId={activeWorkspaceId ?? ""}
            onDealDeleted={() =>
              activeWorkspaceId && fetchDeals(activeWorkspaceId)
            }
            onDealArchived={() =>
              activeWorkspaceId && fetchDeals(activeWorkspaceId)
            }
          />
        </div>

        {/* Desktop view: table or kanban based on view-switcher */}
        <div className="hidden md:block h-full">
          {view === "kanban" ? (
            <KanbanView
              deals={filteredDeals}
              onStatusChange={editDealStatusMutation.mutate}
              workspaceId={activeWorkspaceId ?? ""}
              onDealDeleted={() =>
                activeWorkspaceId && fetchDeals(activeWorkspaceId)
              }
              onDealArchived={() =>
                activeWorkspaceId && fetchDeals(activeWorkspaceId)
              }
            />
          ) : (
            <TableView
              deals={filteredDeals}
              onStatusChange={editDealStatusMutation.mutate}
              workspaceId={activeWorkspaceId ?? ""}
              onDealDeleted={() =>
                activeWorkspaceId && fetchDeals(activeWorkspaceId)
              }
              onDealArchived={() =>
                activeWorkspaceId && fetchDeals(activeWorkspaceId)
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};
